/**
 * @rogueworlds/client — the terrain texture atlas, generated procedurally at startup.
 *
 * One 4x4 grid of 16x16 px tiles: tile index === BlockId (`packages/voxel-bridge` is explicit that
 * `BLOCKS[id].id === id`, which is what lets the mesher put a `BlockId` in `MeshData.tiles` and the
 * renderer turn it into an atlas cell with no second lookup table). Tile 0 is deliberately unused
 * by the registry (`AIR`) and is painted as a loud magenta checkerboard so a tile-mapping bug is
 * visible instead of silent; tiles 1..11 are STONE, DIRT, GRASS, SAND, WATER, LOG, LEAVES, PLANK,
 * GLASS, SNOW, BASALT.
 *
 * Every pixel is a deterministic function of `(tile, x, y)` through a small integer hash — no
 * `Math.random`, no `Date`, so two runs (and two machines) produce the identical atlas, and the
 * client never disagrees with the server about what a block looks like.
 *
 * The atlas is 64x64 with `NearestFilter`, no mipmaps and clamped edges, and the chunk material
 * repeats one cell per voxel face (see `chunk-renderer.ts`), so a greedy quad spanning many voxels
 * still shows one tile per 10 cm voxel without bleeding into its neighbours.
 *
 * V orientation: `THREE.CanvasTexture` keeps `flipY = true`, so canvas row 0 is v = 1. `atlasCell`
 * is the single source of truth for "tile index -> normalised uv origin" and accounts for that.
 */

import * as THREE from "three";
import { BLOCKS } from "@rogueworlds/voxel-bridge";

export const ATLAS_COLUMNS = 4;
export const ATLAS_ROWS = 4;
/** Edge length of one atlas tile in pixels. */
export const ATLAS_TILE_PX = 16;

const ATLAS_TILES = ATLAS_COLUMNS * ATLAS_ROWS;

/** How a tile's pixels are patterned on top of its block colour. */
type TilePattern = "speckle" | "coarse" | "ripple" | "grainY" | "grainX" | "holes" | "frame" | "cracks" | "missing";

interface TileSpec {
  readonly pattern: TilePattern;
  /** Peak-to-peak brightness swing of the per-pixel hash noise. */
  readonly contrast: number;
}

/** Per-tile look. Anything not listed falls back to a mild speckle. */
const TILE_SPECS: Readonly<Record<number, TileSpec>> = {
  0: { pattern: "missing", contrast: 0 },
  1: { pattern: "speckle", contrast: 0.1 }, // stone
  2: { pattern: "coarse", contrast: 0.16 }, // dirt
  3: { pattern: "speckle", contrast: 0.18 }, // grass
  4: { pattern: "speckle", contrast: 0.07 }, // sand
  5: { pattern: "ripple", contrast: 0.08 }, // water
  6: { pattern: "grainY", contrast: 0.14 }, // log
  7: { pattern: "holes", contrast: 0.2 }, // leaves
  8: { pattern: "grainX", contrast: 0.08 }, // plank
  9: { pattern: "frame", contrast: 0.04 }, // glass
  10: { pattern: "speckle", contrast: 0.05 }, // snow
  11: { pattern: "cracks", contrast: 0.18 }, // basalt
};

const DEFAULT_SPEC: TileSpec = { pattern: "speckle", contrast: 0.12 };

/** Fallback tint when a block id has no registry entry. Deliberately neutral, never black. */
const FALLBACK_COLOR: readonly [number, number, number] = [128, 128, 128];

/**
 * 32-bit integer avalanche hash -> [0, 1). Integer-only (`Math.imul` + xor-shift), so it is exact
 * and identical in every JS engine. `x`/`y` are tile pixels, `salt` separates the noise fields.
 */
function tileNoise(x: number, y: number, salt: number): number {
  let h = Math.imul(x + 1, 0x85ebca6b) ^ Math.imul(y + 1, 0xc2b2ae35) ^ Math.imul(salt + 1, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x2545f491);
  h ^= h >>> 13;
  h = Math.imul(h, 0x27d4eb2f);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Block colour as sRGB bytes. `BlockDef.color` is 0..1 per channel. */
function blockColorBytes(tile: number): readonly [number, number, number] {
  const def = BLOCKS[tile];
  if (def === undefined) {
    return FALLBACK_COLOR;
  }
  const [r, g, b] = def.color;
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function clampByte(value: number): number {
  if (value < 0) {
    return 0;
  }
  if (value > 255) {
    return 255;
  }
  return Math.round(value);
}

function isTileBorder(px: number, py: number): boolean {
  return px === 0 || py === 0 || px === ATLAS_TILE_PX - 1 || py === ATLAS_TILE_PX - 1;
}

/**
 * Brightness multiplier for one pixel of one tile. The pattern is what makes a block recognisable
 * at 16x16 (vertical log grain, plank seams, leaf holes, water ripples, basalt cracks), and the
 * border shading is what makes a repeating 10 cm voxel read as a voxel rather than as noise.
 */
function pixelShade(spec: TileSpec, px: number, py: number, tile: number, noise: number): number {
  let shade = 1 + (noise - 0.5) * spec.contrast * 2;

  switch (spec.pattern) {
    case "coarse":
      // Dirt: a few larger dark clods plus sparse light grit.
      if (noise > 0.78) {
        shade *= 0.78;
      } else if (tileNoise(px >> 1, py >> 1, tile + 31) < 0.18) {
        shade *= 1.12;
      }
      break;
    case "ripple":
      // Water: horizontal bands, two pixels tall, so the surface reads as moving even when static.
      shade *= tileNoise(0, py, tile + 47) < 0.34 ? 1.07 : 0.96;
      break;
    case "grainY":
      // Log: vertical grain lines every ~4 px.
      if (tileNoise(px, 0, tile + 7) > 0.7) {
        shade *= 0.8;
      }
      break;
    case "grainX":
      // Plank: a dark seam every 8 px with a lighter board above it.
      shade *= py % 8 === 0 ? 0.72 : py % 8 === 1 ? 1.08 : 1;
      break;
    case "holes":
      // Leaves: dark gaps punched through the canopy.
      if (noise > 0.8) {
        shade *= 0.68;
      } else if (noise < 0.14) {
        shade *= 1.08;
      }
      break;
    case "frame":
      // Glass: a bright pane edge plus one diagonal highlight.
      if (isTileBorder(px, py)) {
        shade *= 1.16;
      } else if (px + py === 6 || px + py === 7) {
        shade *= 1.12;
      }
      break;
    case "cracks":
      // Basalt: sparse dark veins.
      if (noise > 0.9 || tileNoise(px, py >> 2, tile + 13) > 0.88) {
        shade *= 0.62;
      }
      break;
    case "speckle":
      if (noise > 0.86) {
        shade *= 0.86;
      } else if (noise < 0.1) {
        shade *= 1.07;
      }
      break;
    case "missing":
      break;
  }

  if (spec.pattern !== "missing" && isTileBorder(px, py)) {
    shade *= 0.9;
  }
  return shade;
}

/** Writes one tile into the atlas pixel buffer. */
function paintTile(
  pixels: Uint8ClampedArray,
  atlasPx: number,
  tile: number,
  spec: TileSpec,
  base: readonly [number, number, number],
): void {
  const originX = (tile % ATLAS_COLUMNS) * ATLAS_TILE_PX;
  const originY = Math.floor(tile / ATLAS_COLUMNS) * ATLAS_TILE_PX;

  for (let py = 0; py < ATLAS_TILE_PX; py++) {
    for (let px = 0; px < ATLAS_TILE_PX; px++) {
      const offset = ((originY + py) * atlasPx + originX + px) * 4;
      if (spec.pattern === "missing") {
        // 4x4 magenta/black checkerboard: an unmapped tile is a loud bug, never a quiet grey.
        const checker = ((px >> 2) + (py >> 2)) % 2 === 0;
        pixels[offset] = checker ? 255 : 26;
        pixels[offset + 1] = checker ? 0 : 26;
        pixels[offset + 2] = checker ? 255 : 26;
        pixels[offset + 3] = 255;
        continue;
      }
      const shade = pixelShade(spec, px, py, tile, tileNoise(px, py, tile));
      pixels[offset] = clampByte(base[0] * shade);
      pixels[offset + 1] = clampByte(base[1] * shade);
      pixels[offset + 2] = clampByte(base[2] * shade);
      pixels[offset + 3] = 255;
    }
  }
}

/**
 * Builds the atlas on a 2D canvas and wraps it as a texture. Called once per client; the result is
 * shared by every chunk mesh, so it counts as one texture in `renderer.info.memory.textures`.
 */
export function createAtlasTexture(): THREE.Texture {
  const atlasPx = ATLAS_COLUMNS * ATLAS_TILE_PX;
  const canvas = document.createElement("canvas");
  canvas.width = atlasPx;
  canvas.height = atlasPx;
  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("createAtlasTexture: this browser has no 2D canvas context");
  }

  const image = context.createImageData(atlasPx, atlasPx);
  for (let tile = 0; tile < ATLAS_TILES; tile++) {
    paintTile(image.data, atlasPx, tile, TILE_SPECS[tile] ?? DEFAULT_SPEC, blockColorBytes(tile));
  }
  context.putImageData(image, 0, 0);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  // Nearest + no mipmaps: texel centres are addressed explicitly by the chunk material, and mips
  // would blend across cell borders at grazing angles.
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

/**
 * Normalised uv origin of a tile's cell. This is the one place that knows the grid layout, and the
 * one place that compensates for `CanvasTexture` flipping V, so the shader can stay dumb:
 * `atlasUv = cell + local / vec2(ATLAS_COLUMNS, ATLAS_ROWS)`.
 *
 * Unknown or fractional indices clamp to tile 0 (the loud "missing tile" cell).
 */
export function atlasCell(tileIndex: number): { u: number; v: number } {
  const tile = Number.isFinite(tileIndex) ? Math.floor(tileIndex) : 0;
  if (tile < 0 || tile >= ATLAS_TILES) {
    return { u: 0, v: (ATLAS_ROWS - 1) / ATLAS_ROWS };
  }
  const column = tile % ATLAS_COLUMNS;
  const row = Math.floor(tile / ATLAS_COLUMNS);
  return { u: column / ATLAS_COLUMNS, v: (ATLAS_ROWS - 1 - row) / ATLAS_ROWS };
}
